import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserCircleIcon, FileArrowDownIcon, FileTextIcon } from "@phosphor-icons/react";
import { detectRegion, formatVolume, formatCurrency, formatPricePerUnit, formatEconomy, formatDistance } from "@/lib/units";

/**
 * Admin-side receipt viewer for a single uploaded fuel-up event.
 * Shows a summary (gallons, cost, $/gal, station) and renders each
 * driver-uploaded attachment (image inline or PDF/other with download).
 */
export default function FuelReceiptViewer({ open, onClose, event }) {
  if (!event) return null;
  const gallons = event.actual_gallons ?? event.estimated_gallons;
  const cost = event.cost;
  const perGal = gallons && cost ? Number(cost) / Number(gallons) : null;
  const uploaded = event.receipt_uploaded_at ? new Date(event.receipt_uploaded_at).toLocaleDateString() : "—";
  // Volume + distance + economy follow the TRUCK's unit system (server-resolved
  // and attached as truck_unit_system on each fill-up row).
  // Currency follows the fill-up LOCATION so a US receipt still shows US$.
  const unitRegion = event.truck_unit_system === "metric" ? "CA" : "US";
  const currencyRegion = detectRegion({ location_address: event.location_address, address: event.notes });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto p-0" data-testid="fuel-receipt-viewer">
        <div className="bg-slate-900 text-white p-6 sticky top-0 z-10">
          <DialogHeader>
            <div className="section-eyebrow text-blue-400 mb-1">/// Receipt</div>
            <DialogTitle className="font-display font-black text-3xl tracking-tighter text-white">
              Truck #{event.unit_number}
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-4 bg-slate-50">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center gap-2 text-sm text-slate-700 mb-3">
              <UserCircleIcon size={18} weight="bold" className="text-slate-500" />
              <span className="font-bold uppercase font-mono tracking-wide text-xs">{event.driver_name || "—"}</span>
              <span className="text-slate-400">·</span>
              <span className="text-xs text-slate-500 font-mono">Uploaded {uploaded}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <StatTile label={unitRegion === "CA" ? "Litres" : "Gallons"} value={formatVolume(gallons, unitRegion, 1).replace(/ (gal|L)$/, "")} highlight />
              <StatTile label="Total cost" value={formatCurrency(cost, currencyRegion)} highlight />
              <StatTile label={unitRegion === "CA" ? `${currencyRegion === "CA" ? "CA$" : "US$"} / L` : `${currencyRegion === "CA" ? "CA$" : "US$"} / gal`} value={perGal != null ? formatPricePerUnit(perGal, unitRegion, 3, currencyRegion) : "—"} />
              <StatTile label="Station" value={event.receipt_station || event.notes || "—"} />
            </div>
            {event.mileage && (
              <div className="mt-3 text-xs font-mono text-slate-500">
                Odometer at fill: <span className="font-bold text-slate-700">{formatDistance(event.mileage, unitRegion, 0)}</span>
                {event.mpg_since_last != null && (
                  <span className="ml-3">Economy: <span className="font-bold text-slate-700">{formatEconomy(event.mpg_since_last, unitRegion)}</span></span>
                )}
              </div>
            )}

            {/* AI-extracted receipt breakdown */}
            {event.receipt_parsed && (
              <div className="mt-4 rounded-sm border border-blue-200 bg-blue-50 p-3" data-testid="receipt-parsed">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-blue-800 font-bold">/// AI-extracted from receipt</div>
                  <span className="text-[9px] font-mono text-blue-700 bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded-sm">Gemini</span>
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {[
                    ["Station", event.receipt_parsed.station_name],
                    ["Address", event.receipt_parsed.station_address],
                    ["Date/time", event.receipt_parsed.transaction_datetime],
                    ["Fuel type", event.receipt_parsed.fuel_type],
                    ["Gallons", event.receipt_parsed.gallons],
                    ["Litres", event.receipt_parsed.litres],
                    ["Price / unit", event.receipt_parsed.price_per_unit != null ? `${event.receipt_parsed.price_currency || ""} ${event.receipt_parsed.price_per_unit}/${event.receipt_parsed.unit_shown_on_receipt || ""}` : null],
                    ["Subtotal", event.receipt_parsed.subtotal],
                    ["Tax", event.receipt_parsed.tax],
                    ["Total", event.receipt_parsed.total_cost != null ? `${event.receipt_parsed.price_currency || ""} ${event.receipt_parsed.total_cost}` : null],
                    ["Payment", event.receipt_parsed.payment_method],
                    ["Pump", event.receipt_parsed.pump_number],
                    ["Receipt #", event.receipt_parsed.receipt_number],
                  ]
                    .filter(([, v]) => v != null && v !== "")
                    .map(([k, v]) => (
                      <React.Fragment key={k}>
                        <dt className="text-slate-600 font-mono uppercase text-[10px]">{k}</dt>
                        <dd className="text-slate-900 font-mono truncate">{String(v)}</dd>
                      </React.Fragment>
                    ))}
                </dl>
              </div>
            )}
          </div>

          {/* Receipt attachments */}
          <div className="space-y-3" data-testid="receipt-attachments">
            {(event.receipt_attachments || []).length === 0 ? (
              <div className="bg-white border border-dashed border-slate-300 rounded-sm p-6 text-center text-sm text-slate-500">
                No receipt files attached.
              </div>
            ) : (
              (event.receipt_attachments || []).map((att, i) => (
                <Attachment key={i} att={att} />
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatTile({ label, value, highlight = false }) {
  return (
    <div className="bg-slate-100 rounded-sm p-3">
      <div className="font-mono text-[10px] uppercase text-slate-500 tracking-wider font-bold">{label}</div>
      <div className={`font-display font-black text-2xl tracking-tighter mt-1 tabular-nums ${highlight ? "text-blue-600" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}

function Attachment({ att }) {
  const isImage = (att.type || "").startsWith("image/");
  const src = att.data_base64
    ? `data:${att.type || "image/jpeg"};base64,${att.data_base64}`
    : att.url || "";
  if (isImage && src) {
    return (
      <a href={src} download={att.name} className="block bg-white border border-slate-800 rounded-sm overflow-hidden shadow-sm">
        <img src={src} alt={att.name || "receipt"} className="w-full block" />
      </a>
    );
  }
  return (
    <a
      href={src}
      download={att.name}
      className="flex items-center gap-3 bg-white border border-slate-200 rounded-sm p-4 hover:bg-slate-50"
    >
      <FileTextIcon size={28} weight="bold" className="text-blue-600" />
      <div className="flex-1">
        <div className="font-bold text-sm">{att.name || "receipt"}</div>
        <div className="text-xs text-slate-500 font-mono">{Math.round((att.size || 0) / 1024)} KB · {att.type || "file"}</div>
      </div>
      <FileArrowDownIcon size={22} weight="bold" className="text-slate-500" />
    </a>
  );
}
